import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import Img from "@/components/Img";

export const STANCOR_PATH = "/joint-ventures/stancor-group";

/** The anchor-partner lockup: their mark beside the group's own typography. */
export const SpotlightLockup = ({ spotlight, size = "md" }) => {
  const mark = size === "lg" ? "h-16 w-16 md:h-20 md:w-20" : "h-12 w-12 md:h-14 md:w-14";
  const type = size === "lg" ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl";
  return (
    <div className="flex items-center gap-4" data-testid="spotlight-lockup">
      {spotlight.logo ? (
        <Img
          src={spotlight.logo}
          width={320}
          sizes="80px"
          alt={spotlight.name}
          className={`${mark} shrink-0 rounded-xl object-contain`}
        />
      ) : null}
      <p className={`font-head leading-none ${type}`}>{spotlight.name}</p>
    </div>
  );
};

/** `undefined` while the site payload is still in flight, `null` when switched off. */
export const useSpotlight = (placement) => {
  const site = useSite();
  if (!site?.settings) return undefined;
  const s = site.settings.spotlight;
  if (!s || !s.enabled || !s.name) return null;
  if (placement && s[placement] === false) return null;
  return s;
};

/**
 * The full-width strategic partner stage. Used on the homepage and, in a
 * tighter form, at the top of the Joint Ventures page.
 */
export const SpotlightSection = ({ placement = "show_home", compact = false }) => {
  const { t, L } = useLang();
  const s = useSpotlight(placement);
  if (!s) return null;

  const paragraphs = (L(s.statement) || "").split("\n\n").filter(Boolean);

  return (
    <section
      className="relative overflow-hidden border-y border-primary/25 bg-primary/[0.04]"
      data-testid={`spotlight-section-${compact ? "ventures" : "home"}`}
    >
      <span aria-hidden className="pointer-events-none absolute -end-24 -top-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-24">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          <Reveal>
            <p className="eyebrow text-primary">{L(s.eyebrow) || t("spotlight_eyebrow")}</p>
            <div className="mt-5">
              <SpotlightLockup spotlight={s} size={compact ? "md" : "lg"} />
            </div>
            {L(s.role) ? (
              <p className="mt-4 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground" data-testid="spotlight-role">
                {L(s.role)}
              </p>
            ) : null}
            {L(s.tagline) ? (
              <p className="mt-6 max-w-xl font-head text-2xl leading-snug md:text-3xl" data-testid="spotlight-tagline">
                {L(s.tagline)}
              </p>
            ) : null}
            <div className="mt-8 flex flex-wrap items-center gap-5">
              {s.show_page !== false && (
                <Link to={STANCOR_PATH} className="btn-gold" data-testid="spotlight-page-link">
                  {t("spotlight_cta")} <ArrowUpRight className="ms-2 inline h-4 w-4" />
                </Link>
              )}
              {s.link ? (
                <a
                  href={s.link}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-sm font-semibold text-primary hover:underline"
                  data-testid="spotlight-external-link"
                >
                  {t("spotlight_visit")}
                </a>
              ) : null}
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            {!compact && paragraphs.length > 0 && (
              <p className="text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="spotlight-statement">
                {paragraphs[0]}
              </p>
            )}
            {s.figures?.length ? (
              <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-7" data-testid="spotlight-figures">
                {s.figures.map((f, i) => (
                  <div key={i} className="border-s border-primary/30 ps-4">
                    <dt className="font-head text-2xl text-primary md:text-3xl" data-testid={`spotlight-figure-${i}`}>{f.value}</dt>
                    <dd className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{L(f.label)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {s.approvals?.length ? (
              <ul className="mt-9 flex flex-wrap gap-2" data-testid="spotlight-approvals">
                {s.approvals.map((a) => (
                  <li
                    key={a}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-[11px] font-semibold text-muted-foreground"
                  >
                    <BadgeCheck className="h-3.5 w-3.5 text-primary" /> {a}
                  </li>
                ))}
              </ul>
            ) : null}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default SpotlightSection;
